import { UUID } from 'node:crypto';
import { ChangeDetectionStrategy, Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EmployeeNoteService, IEmployeeNote } from '@shared/services/employee-note.service';
import { ValidatorPatterns } from '@shared/validatorpatterns';
import { forkJoin, Observable } from 'rxjs';

export interface IEmployeeNoteEditView {
	id: UUID;
	note: string;
}

export interface IEmployeeNotesEditView {
	employeeId: string;
	date: Date;
	notes: IEmployeeNoteEditView[];
}

@Component({
	selector: 'app-edit-employee-notes',
	templateUrl: './edit-employee-notes.component.html',
	styleUrls: ['./edit-employee-notes.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class EditEmployeeNotesComponent implements OnInit {
	form: FormGroup;
	httpError: string;
	loading: boolean;
	deletedNoteControls: FormGroup[] = [];
	@Output() updated: EventEmitter<IEmployeeNote[]> = new EventEmitter();

	constructor(
		public dialogRef: MatDialogRef<EditEmployeeNotesComponent>,
		private fb: FormBuilder,
		private employeeNoteService: EmployeeNoteService,
		@Inject(MAT_DIALOG_DATA) public data: IEmployeeNotesEditView,
	) {}

	ngOnInit(): void {
		this.initializeForm();
	}

	initializeForm(): void {
		this.form = this.fb.group({
			newNote: ['', [Validators.maxLength(512), Validators.pattern(ValidatorPatterns.Description)]],
			notes: this.fb.array([]),
		});

		this.data.notes.forEach((c) =>
			this.notesControlArray.push(
				this.fb.group({
					note: [c.note, [Validators.maxLength(512), Validators.pattern(ValidatorPatterns.Description)]],
					id: [c.id],
				}),
			),
		);
	}

	get notesControlArray(): FormArray<FormGroup> {
		return this.form.get('notes') as FormArray<FormGroup>;
	}

	getFirstValidationErrorName(formControlName: string) {
		return Object.keys(this.form.controls[formControlName].errors)[0];
	}

	get controls() {
		return this.form.controls;
	}

	onDelete(formGroup: FormGroup) {
		this.deletedNoteControls.push(formGroup);
		const i = this.notesControlArray.controls.indexOf(formGroup);
		this.notesControlArray.removeAt(i);
	}

	onCancel = () => this.dialogRef.close();

	onSave() {
		const observables: Observable<any>[] = [];
		const date = new Date(this.data.date);

		// Save new
		const newValue = (this.form.get('newNote') as FormControl<string>).value.trim();
		if (newValue !== '') {
			observables.push(
				this.employeeNoteService.createNote({
					note: newValue,
					employeeId: this.data.employeeId,
					date,
					id: null,
					modifiedDate: null,
					modifiedByUserId: null,
				}),
			);
		}

		// Save changed
		this.notesControlArray.controls.forEach((c) => {
			if (c.controls['note'].dirty) {
				const request: IEmployeeNote = {
					id: c.controls['id'].value as UUID,
					date,
					employeeId: this.data.employeeId,
					note: c.controls['note'].value as string,
					modifiedDate: null,
					modifiedByUserId: null,
				};
				observables.push(this.employeeNoteService.updateNote(request));
			}
		});

		// Save deleted
		this.deletedNoteControls.forEach((c) => observables.push(this.employeeNoteService.deleteNote(c.controls['id'].value)));

		// Wait for all to complete
		forkJoin(observables).subscribe({
			error: (errorResponse) => {
				this.httpError = `${errorResponse.status}`;
				this.loading = false;
			},
			complete: () => {
				this.updated.emit([]);
				this.dialogRef.close();
			},
		});
	}
}
